import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";

// Public / landing surfaces — keep eager so first paint is instant.
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AppShell from "@/components/AppShell";

// Protected + infrequent pages — code-split so heavy deps (recharts, pdf
// helpers, charts, chat transcripts) never block the sign-in critical path.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const InvoiceEditor = lazy(() => import("@/pages/InvoiceEditor"));
const InvoiceView = lazy(() => import("@/pages/InvoiceView"));
const Clients = lazy(() => import("@/pages/Clients"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const Cfo = lazy(() => import("@/pages/Cfo"));
const Settings = lazy(() => import("@/pages/Settings"));
const Banks = lazy(() => import("@/pages/Banks"));
const Receipts = lazy(() => import("@/pages/Receipts"));
const Products = lazy(() => import("@/pages/Products"));
const Recurring = lazy(() => import("@/pages/Recurring"));
const Accounting = lazy(() => import("@/pages/Accounting"));
const PublicInvoice = lazy(() => import("@/pages/PublicInvoice"));
const Team = lazy(() => import("@/pages/Team"));
const Approvals = lazy(() => import("@/pages/Approvals"));
const Developer = lazy(() => import("@/pages/Developer"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const Budgets = lazy(() => import("@/pages/Budgets"));
const Docs = lazy(() => import("@/pages/Docs"));
const ClientPortal = lazy(() => import("@/pages/ClientPortal"));
const Bills = lazy(() => import("@/pages/Bills"));
const Vendors = lazy(() => import("@/pages/Vendors"));
const Memberships = lazy(() => import("@/pages/Memberships"));
const PublicMembership = lazy(() => import("@/pages/PublicMembership"));
const MembershipCard = lazy(() => import("@/pages/MembershipCard"));
const Scanner = lazy(() => import("@/pages/Scanner"));
const PlatformAdmin = lazy(() => import("@/pages/PlatformAdmin"));
const Learn = lazy(() => import("@/pages/Learn"));
const Liquidity = lazy(() => import("@/pages/Liquidity"));
const Scenarios = lazy(() => import("@/pages/Scenarios"));
const Signals = lazy(() => import("@/pages/Signals"));
const Saas = lazy(() => import("@/pages/Saas"));
const Fundraising = lazy(() => import("@/pages/Fundraising"));
const Performance = lazy(() => import("@/pages/Performance"));
const Licenses = lazy(() => import("@/pages/Licenses"));
const Forecast = lazy(() => import("@/pages/Forecast"));
const Migrate = lazy(() => import("@/pages/Migrate"));

function PageSpinner() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="page-loading">
      <div className="h-3 w-3 animate-pulse-dot rounded-full bg-primary" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-3 w-3 animate-pulse-dot rounded-full bg-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Suspense fallback={<PageSpinner />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              <Route path="/p/invoice/:token" element={<PublicInvoice />} />
              <Route path="/p/client/:token" element={<ClientPortal />} />
              <Route path="/m/:orgSlug/:programSlug" element={<PublicMembership />} />
              <Route path="/m/:orgSlug/:programSlug/card/:memberId" element={<MembershipCard />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />

              {/* Public Learn — browse KB without signup */}
              <Route path="/learn" element={<Learn />} />
              <Route path="/learn/:slug" element={<Learn />} />

              <Route
                path="/app"
                element={
                  <Protected>
                    <AppShell />
                  </Protected>
                }
              >
                <Route index element={<Navigate to="/app/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="invoices/new" element={<InvoiceEditor />} />
                <Route path="invoices/:id" element={<InvoiceView />} />
                <Route path="invoices/:id/edit" element={<InvoiceEditor />} />
                <Route path="clients" element={<Clients />} />
                <Route path="products" element={<Products />} />
                <Route path="expenses" element={<Expenses />} />
                <Route path="recurring" element={<Recurring />} />
                <Route path="banks" element={<Banks />} />
                <Route path="receipts" element={<Receipts />} />
                <Route path="cfo" element={<Cfo />} />
                <Route path="accounting" element={<Accounting />} />
                <Route path="team" element={<Team />} />
                <Route path="approvals" element={<Approvals />} />
                <Route path="developer" element={<Developer />} />
                <Route path="budgets" element={<Budgets />} />
                <Route path="bills" element={<Bills />} />
                <Route path="vendors" element={<Vendors />} />
                <Route path="memberships" element={<Memberships />} />
                <Route path="scanner" element={<Scanner />} />
                <Route path="platform" element={<PlatformAdmin />} />
                <Route path="docs" element={<Docs />} />
                <Route path="learn" element={<Learn />} />
                <Route path="learn/:slug" element={<Learn />} />
                <Route path="liquidity" element={<Liquidity />} />
                <Route path="scenarios" element={<Scenarios />} />
                <Route path="signals" element={<Signals />} />
                <Route path="saas" element={<Saas />} />
                <Route path="fundraising" element={<Fundraising />} />
                <Route path="performance" element={<Performance />} />
                <Route path="licenses" element={<Licenses />} />
                <Route path="forecast" element={<Forecast />} />
                <Route path="migrate" element={<Migrate />} />
                <Route path="settings" element={<Settings />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
